import { useEffect, useState, useMemo } from "react";
import { AppLayout } from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LineChart, Activity } from "lucide-react";
import { Link } from "react-router-dom";

// Static bot name lookup — mirrors backend BOTS_CONFIG ids
const BOT_NAMES = { 1: "Bot-1 Nova", 2: "Bot-2 Pulse", 3: "Bot-3 Quantum", 4: "Bot-4 Apex" };
const BOT_COLORS = { 1: "#8AE234", 2: "#00E5FF", 3: "#A855F7", 4: "#F97316" };

export default function Trades() {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [botFilter, setBotFilter] = useState("all"); // "all" | number

  useEffect(() => {
    (async () => {
      try {
        const [tRes, iRes] = await Promise.all([
          api.get("/trades/my?limit=300"),
          api.get("/bots/my-investments").catch(() => ({ data: { investments: [] } })),
        ]);
        setTrades(tRes.data.trades || []);
        setInvestments((iRes.data.investments || []).filter(i => i.status === "running"));
      } catch { /* silent */ }
    })();
  }, []);

  const visibleTrades = useMemo(() => (
    botFilter === "all" ? trades : trades.filter(t => t.bot_id === botFilter)
  ), [trades, botFilter]);

  const totalPnl = visibleTrades.reduce((s, t) => s + t.pnl, 0);
  const bestTrade = visibleTrades.reduce((best, t) => (best === null || t.pnl > best.pnl) ? t : best, null);
  const activeBotIds = investments.map(i => i.bot_id);

  // Per-bot PnL summary across ALL trades (unfiltered) so users see the split
  const perBotPnl = useMemo(() => {
    const map = new Map();
    for (const t of trades) {
      const k = t.bot_id ?? "unknown";
      const cur = map.get(k) || { pnl: 0, count: 0 };
      cur.pnl += t.pnl;
      cur.count += 1;
      map.set(k, cur);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] || 0) - (b[0] || 0));
  }, [trades]);

  return (
    <AppLayout variant="user">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-nb-cyan">Trade History</div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1 flex items-center gap-3"><LineChart className="text-[#8AE234]"/> Your Bot Trades</h1>
        </div>
        {activeBotIds.length === 0 && <Link to="/bots" className="nb-btn-primary text-sm">Activate a bot</Link>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="nb-card p-4"><div className="text-xs text-slate-500 uppercase tracking-widest">Total Trades</div><div className="font-mono text-2xl text-white mt-1">{visibleTrades.length}</div></div>
        <div className="nb-card p-4"><div className="text-xs text-slate-500 uppercase tracking-widest">Cumulative PnL</div><div className={`font-mono text-2xl mt-1 ${totalPnl >= 0 ? "text-[#8AE234]" : "text-red-400"}`}>{totalPnl >= 0 ? "+" : "-"}${Math.abs(totalPnl).toFixed(4)}</div></div>
        <div className="nb-card p-4"><div className="text-xs text-slate-500 uppercase tracking-widest">Best Trade</div><div className="font-mono text-xl text-nb-cyan mt-1">{bestTrade ? `+$${bestTrade.pnl.toFixed(4)}` : "—"}</div><div className="text-[10px] text-slate-500">{bestTrade?.pair}</div></div>
        <div className="nb-card p-4" data-testid="active-bots-summary">
          <div className="text-xs text-slate-500 uppercase tracking-widest">Active Bots</div>
          {activeBotIds.length === 0 ? (
            <div className="font-mono text-2xl text-white mt-1">—</div>
          ) : (
            <div className="mt-1 flex flex-wrap gap-1">
              {activeBotIds.map(id => (
                <span key={id} data-testid={`active-bot-chip-${id}`} className="font-mono text-xs px-2 py-0.5 rounded-full border" style={{color: BOT_COLORS[id], borderColor: (BOT_COLORS[id]||"#94A3B8") + "55", background: (BOT_COLORS[id]||"#94A3B8") + "10"}}>
                  Bot-{id}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Per-bot PnL split — makes it obvious which bot earned what */}
      {perBotPnl.length > 0 && (
        <div className="nb-card p-4 mb-4 overflow-hidden" data-testid="per-bot-pnl-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-slate-400">Per-Bot Earnings</div>
            <div className="flex items-center gap-2">
              <button data-testid="bot-filter-all" onClick={()=>setBotFilter("all")} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full ${botFilter==="all"?"bg-white/10 text-white":"text-slate-400 hover:text-white"}`}>All</button>
              {perBotPnl.map(([bid]) => (
                <button key={bid} data-testid={`bot-filter-${bid}`} onClick={()=>setBotFilter(bid)} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full ${botFilter===bid?"bg-white/10 text-white":"text-slate-400 hover:text-white"}`}>Bot-{bid}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {perBotPnl.map(([bid, s]) => (
              <div key={bid} data-testid={`per-bot-card-${bid}`} className="rounded-xl border border-white/5 bg-[#070B14] p-3">
                <div className="text-[10px] uppercase tracking-widest" style={{color: BOT_COLORS[bid]}}>{BOT_NAMES[bid] || `Bot-${bid}`}</div>
                <div className={`font-mono text-lg mt-0.5 ${s.pnl >= 0 ? "text-[#8AE234]" : "text-red-400"}`}>{s.pnl >= 0 ? "+" : "-"}${Math.abs(s.pnl).toFixed(4)}</div>
                <div className="text-[10px] text-slate-500">{s.count} trades</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="nb-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#070B14] text-xs uppercase text-slate-500 tracking-widest">
            <tr>
              <th className="text-left px-3 py-3">Bot</th>
              <th className="text-left px-3 py-3">Pair</th>
              <th className="text-left px-3 py-3">Side</th>
              <th className="text-right px-3 py-3">Entry</th>
              <th className="text-right px-3 py-3">Exit</th>
              <th className="text-right px-3 py-3">Duration</th>
              <th className="text-right px-3 py-3">PnL</th>
              <th className="text-right px-3 py-3">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {visibleTrades.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-slate-500 text-sm">
                <Activity size={20} className="mx-auto mb-2 text-slate-600"/>
                No trades yet. Once your bot runs, trade history appears here.
              </td></tr>
            ) : visibleTrades.map((t) => (
              <tr key={t.id} data-testid={`trade-${t.id}`}>
                <td className="px-3 py-3">
                  <span data-testid={`trade-bot-${t.id}`} className="font-mono text-[11px] px-2 py-0.5 rounded-full border" style={{color: BOT_COLORS[t.bot_id] || "#94A3B8", borderColor: (BOT_COLORS[t.bot_id] || "#94A3B8") + "55", background: (BOT_COLORS[t.bot_id] || "#94A3B8") + "10"}}>
                    Bot-{t.bot_id ?? "?"}
                  </span>
                </td>
                <td className="px-3 py-3 font-mono text-white">{t.pair}</td>
                <td className="px-3 py-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${t.side==="LONG"?"bg-[#8AE234]/10 text-[#8AE234]":"bg-red-500/10 text-red-400"}`}>{t.side}</span></td>
                <td className="px-3 py-3 text-right font-mono text-slate-300">${t.entry < 10 ? t.entry.toFixed(4) : t.entry.toFixed(2)}</td>
                <td className="px-3 py-3 text-right font-mono text-slate-300">${t.exit < 10 ? t.exit.toFixed(4) : t.exit.toFixed(2)}</td>
                <td className="px-3 py-3 text-right text-xs text-slate-400">{t.duration_min}m</td>
                <td className={`px-3 py-3 text-right font-mono ${t.pnl >= 0 ? "text-[#8AE234]" : "text-red-400"}`}>{t.pnl >= 0 ? "+" : "-"}${Math.abs(t.pnl).toFixed(4)}</td>
                <td className="px-3 py-3 text-right text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
